/*
 * Khối dựng trang dùng chung của Simba (giống hệt Rafiki — bộ Design System dùng chung). MỌI trang dựng từ các khối này +
 * component shadcn (src/components/ui) + utility Tailwind — không viết CSS riêng.
 *
 *   <Page>
 *     <PageHeader eyebrow="LEARNER WORK" title="Submissions" description="…" actions={…} />
 *     <Card>…</Card>
 *   </Page>
 */
import { useEffect, type ReactNode } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Spinner } from "@/components/ui/spinner"
import { Icon, type AnyIconName } from "@/components/icon"
import { cn } from "@/lib/utils"

/** Khung một trang: cột nội dung tối đa 1120px, khoảng cách dọc đều. */
export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <div className={cn("mx-auto flex w-full flex-col gap-6", wide ? "max-w-[1320px]" : "max-w-[1120px]", className)}>
      {children}
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 max-w-3xl flex-col gap-2">
        {eyebrow && <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">{eyebrow}</p>}
        <h1 className="text-3xl leading-tight font-bold sm:text-4xl">{title}</h1>
        {description && <p className="text-base text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

/** Tiêu đề một khu vực bên trong trang. */
export function SectionTitle({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function EmptyState({
  icon = "sparkles",
  mascot,
  title,
  description,
  action,
  className,
}: {
  icon?: AnyIconName
  /** tên mascot 2D, vd "meerkat-thinking" — dùng thay icon cho trạng thái trống lớn */
  mascot?: string
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <Empty className={cn("rounded-[20px] border-[1.5px] border-dashed border-edge bg-card/60 py-12", className)}>
      <EmptyHeader>
        <EmptyMedia>
          {mascot ? (
            <img src={`/brand/sutucon/mascots-2d/${mascot}.svg`} alt="" className="size-24" />
          ) : (
            <span className="grid size-14 place-items-center rounded-2xl bg-soft">
              <Icon name={icon} className="size-7" />
            </span>
          )}
        </EmptyMedia>
        <EmptyTitle className="font-heading text-lg text-ink">{title}</EmptyTitle>
        {description && <EmptyDescription className="text-sm">{description}</EmptyDescription>}
      </EmptyHeader>
      {action && <EmptyContent>{action}</EmptyContent>}
    </Empty>
  )
}

export function LoadingState({ label = "Đang tải…", className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex items-center justify-center gap-3 py-16 text-sm text-muted-foreground", className)}>
      <Spinner className="size-5 text-orange" />
      {label}
    </div>
  )
}

export function ErrorState({ message, onRetry, className }: { message: string; onRetry?: () => void; className?: string }) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-2xl border-[1.5px] border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger",
        className,
      )}
    >
      <Icon name="bell" className="size-5" />
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Thử lại
        </Button>
      )}
    </div>
  )
}

/** Dòng thông báo kết quả thao tác (đã lưu / lỗi). Không hiện gì khi rỗng. */
export function StatusNote({ children, tone = "info", className }: { children?: ReactNode; tone?: "info" | "success" | "error"; className?: string }) {
  if (!children) return null
  return (
    <p
      role="status"
      className={cn(
        "rounded-xl px-3.5 py-2.5 text-sm",
        tone === "success" && "bg-success-soft text-success",
        tone === "error" && "bg-danger-soft text-danger",
        tone === "info" && "bg-soft text-accent-foreground",
        className,
      )}
    >
      {children}
    </p>
  )
}

const displayName = (user: any) => user?.full_name || user?.name || user?.display_name || user?.email || "Học viên"
const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()

export function UserAvatar({ user, size = "default", className }: { user: any; size?: "sm" | "default" | "lg"; className?: string }) {
  const name = displayName(user)
  return (
    <Avatar size={size} className={className}>
      {user?.avatar_url && <AvatarImage src={user.avatar_url} alt="" referrerPolicy="no-referrer" />}
      <AvatarFallback className="bg-soft font-semibold text-accent-foreground">{initials(name)}</AvatarFallback>
    </Avatar>
  )
}

/** Hiệu ứng vào trang: chỉ dịch chuyển, KHÔNG làm mờ (opacity kẹt = trang trắng). */
export function PageMotion({ children, motionKey }: { children: ReactNode; motionKey: string }) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      key={motionKey}
      initial={reduce ? false : { y: 10 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  )
}

/** Thanh tiến độ mảnh dùng trong bảng/thẻ. */
export function Meter({ value, className }: { value: number; className?: string }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0))
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-line", className)}>
      <div className="h-full rounded-full bg-orange transition-[width] duration-500" style={{ width: `${v}%` }} />
    </div>
  )
}

export const SITE_NAME = "Simba Learner Space"
export const SITE_URL = "https://simba.sutucon.com"

/**
 * Title, description, canonical, robots và thẻ OG riêng cho từng URL (SPA nên
 * phải ghi lại mỗi lần đổi trang). Trang trong lớp học: noIndex.
 */
export function usePageTitle(title: string | undefined, description: string, opts: { noIndex?: boolean } = {}) {
  const noIndex = opts.noIndex ?? true
  useEffect(() => {
    const pageTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Học đúng lớp, tiến bộ theo cách của con`
    const path = location.pathname
    const canonical = `${SITE_URL}${path === "/" ? "" : path.replace(/\/$/, "")}`
    document.title = pageTitle
    const setMeta = (selector: string, attr: string, value: string, create: () => HTMLMetaElement) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector)
      if (!el) document.head.appendChild((el = create()))
      el.setAttribute(attr, value)
    }
    const named = (name: string) => () => Object.assign(document.createElement("meta"), { name })
    const prop = (property: string) => () => {
      const m = document.createElement("meta")
      m.setAttribute("property", property)
      return m
    }
    setMeta('meta[name="description"]', "content", description, named("description"))
    setMeta('meta[name="robots"]', "content", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large", named("robots"))
    setMeta('meta[property="og:title"]', "content", pageTitle, prop("og:title"))
    setMeta('meta[property="og:description"]', "content", description, prop("og:description"))
    setMeta('meta[property="og:url"]', "content", canonical, prop("og:url"))
    setMeta('meta[name="twitter:title"]', "content", pageTitle, named("twitter:title"))
    setMeta('meta[name="twitter:description"]', "content", description, named("twitter:description"))
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) document.head.appendChild((link = Object.assign(document.createElement("link"), { rel: "canonical" })))
    link.href = canonical
  }, [title, description, noIndex])
}
